import { isIndexableLocaleChannel, isValidLocale, type Locale } from "@/config/locale";
import { isSeoContentReleaseLocale } from "@/config/seo-content-release-policy.mjs";

export type AgentPageTarget = {
	locale: string;
	channel: "eu" | "us";
	kind: "home" | "product" | "guide" | "shipping";
	slug?: string;
	variantId?: string;
};

export const AGENT_TARGET_HEADER = "x-bergdorf-agent-target";

export function isAgentMarkdownEnabled(value = process.env.AGENT_MARKDOWN_ENABLED): boolean {
	const normalized = value?.trim().toLowerCase();
	return normalized === "true" || normalized === "1";
}

type MediaRange = { type: string; q: number };

function parseQuality(raw: string | undefined): number {
	if (raw === undefined) return 1;
	if (!/^\d(?:\.\d{0,3})?$/.test(raw.trim())) return 0;
	const quality = Number(raw);
	return quality >= 0 && quality <= 1 ? quality : 0;
}

function parseAccept(accept: string): MediaRange[] {
	return accept.split(",").flatMap((entry) => {
		const [rawType, ...parameters] = entry.split(";");
		const type = rawType?.trim().toLowerCase();
		if (!type || !type.includes("/")) return [];
		const qParameter = parameters.find((parameter) => parameter.trim().toLowerCase().startsWith("q="));
		const q = parseQuality(qParameter?.trim().slice(2));
		return [{ type, q }];
	});
}

/** Markdown must be named explicitly. Explicit HTML wins ties. */
export function prefersMarkdown(acceptHeader: string | null): boolean {
	if (!acceptHeader) return false;
	const ranges = parseAccept(acceptHeader);
	const markdown = ranges
		.filter(({ type }) => type === "text/markdown")
		.reduce((q, item) => Math.max(q, item.q), 0);
	if (markdown <= 0) return false;

	const html = ranges.filter(({ type }) => type === "text/html").reduce((q, item) => Math.max(q, item.q), 0);
	if (html >= markdown) return false;

	const wildcard = ranges
		.filter(({ type }) => type === "*/*" || type === "text/*")
		.reduce((q, item) => Math.max(q, item.q), 0);
	return wildcard <= markdown;
}

function decodeSegment(value: string): string | null {
	try {
		const decoded = decodeURIComponent(value);
		if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/i.test(decoded)) return null;
		return decoded;
	} catch {
		return null;
	}
}

function parseVariantId(value: string | null): string | undefined | null {
	if (value === null) return undefined;
	if (!value || value.length > 255 || /[\u0000-\u001f\\/]/.test(value)) return null;
	return value;
}

/** Parse only canonical, relative storefront paths. Unknown query keys are ignored. */
export function parseMarkdownTarget(relativePathWithOptionalVariantQuery: string): AgentPageTarget | null {
	if (
		!relativePathWithOptionalVariantQuery.startsWith("/") ||
		relativePathWithOptionalVariantQuery.startsWith("//")
	) {
		return null;
	}
	if (
		relativePathWithOptionalVariantQuery.includes("#") ||
		relativePathWithOptionalVariantQuery.includes("\\")
	) {
		return null;
	}
	const rawPath = relativePathWithOptionalVariantQuery.split("?", 1)[0]!;
	for (const rawSegment of rawPath.split("/")) {
		try {
			const decoded = decodeURIComponent(rawSegment);
			if (decoded === "." || decoded === ".." || decoded.includes("/") || decoded.includes("\\")) return null;
		} catch {
			return null;
		}
	}

	let parsed: URL;
	try {
		parsed = new URL(relativePathWithOptionalVariantQuery, "https://agent-target.invalid");
	} catch {
		return null;
	}
	if (parsed.origin !== "https://agent-target.invalid") return null;

	const segments = parsed.pathname.split("/").filter(Boolean);
	if (segments.length < 2) return null;
	const locale = decodeSegment(segments[0]!);
	const channel = decodeSegment(segments[1]!);
	if (!locale || !isValidLocale(locale) || (channel !== "eu" && channel !== "us")) return null;
	if (!isIndexableLocaleChannel(locale as Locale, channel)) return null;

	if (segments.length === 2) return { locale, channel, kind: "home" };
	if (segments.length === 3 && segments[2] === "shipping") {
		return { locale, channel, kind: "shipping" };
	}
	if (segments.length !== 4 || (segments[2] !== "products" && segments[2] !== "guides")) return null;

	const slug = decodeSegment(segments[3]!);
	if (!slug) return null;
	if (segments[2] === "guides") {
		if (!isSeoContentReleaseLocale(slug, locale)) return null;
		return { locale, channel, kind: "guide", slug };
	}

	if (parsed.searchParams.getAll("variant").length > 1) return null;
	const variantId = parseVariantId(parsed.searchParams.get("variant"));
	if (variantId === null) return null;
	return { locale, channel, kind: "product", slug, ...(variantId ? { variantId } : {}) };
}

const PILOT_LOCALE_CHANNELS = new Set(["de/eu", "en/eu", "en/us", "es/us"]);
const PILOT_PRODUCT_SLUGS = new Set(["retatrutide"]);
// Guides remain on HTML until each locale's live body and SEO description have
// an exact reviewed content revision. The loader is ready but no locale is yet verified.
const PILOT_GUIDE_SLUGS = new Set<string>();

export function isMarkdownPilotPath(pathname: string): boolean {
	const target = parseMarkdownTarget(pathname);
	if (!target || !PILOT_LOCALE_CHANNELS.has(`${target.locale}/${target.channel}`)) return false;
	if (target.kind === "product") return Boolean(target.slug && PILOT_PRODUCT_SLUGS.has(target.slug));
	if (target.kind === "guide") return Boolean(target.slug && PILOT_GUIDE_SLUGS.has(target.slug));
	return true;
}

export function isMarkdownRequestExcluded(request: Request): boolean {
	if (request.method !== "GET" && request.method !== "HEAD") return true;
	let url: URL;
	try {
		url = new URL(request.url);
	} catch {
		return true;
	}
	if (url.searchParams.has("_rsc")) return true;
	const headers = request.headers;
	return Boolean(
		headers.has("rsc") ||
		headers.has("next-action") ||
		headers.has("next-router-prefetch") ||
		headers.has("next-router-state-tree") ||
		headers.has("next-router-segment-prefetch") ||
		headers.has("x-middleware-prefetch") ||
		/(?:^|,)\s*prefetch\s*(?:,|$)/i.test(headers.get("purpose") ?? "") ||
		/(?:^|;)\s*prefetch\s*(?:;|$)/i.test(headers.get("sec-purpose") ?? ""),
	);
}
